import { useMappedState } from "redux-react-hook";
import {
  useCallback,
  useMemo
} from "react";
import _ from "lodash";
import moment from "moment-timezone";

import { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import { IOrder } from "src/core/orders/api/orders.api";

import {
  ShutdownDateInfo,
  formatShutdownDates
} from "../utils/formatShutdownDates";

/**
 * Returns an array of all shutdowns that apply to a drop
 * This will include both drop level and global shutdowns
 * */
export const useDropShutdowns = () => {
  const [ { drop } ] = useDrop();
  const globalShutdowns = useMappedState(({ global }: IStore) => (global.settings?.shutdowns || null));

  // return an array of all shutdown dates, both drop and global
  const shutdownDates = useMemo(() => {
    let allShutdowns: ShutdownDateInfo[] = [];

    if (globalShutdowns) {
      allShutdowns = globalShutdowns;
    }

    if (drop?.shutdowns?.length) {
      allShutdowns = _.concat(allShutdowns, formatShutdownDates(drop.shutdowns));
    }

    return allShutdowns;
  }, [ drop?.shutdowns, globalShutdowns ]);

  /**
   * Checks if a date matches shutdown and returns the details of the shutdown if it does
   */
  const checkShutdownDate = useCallback((date: Date, orderType?: IOrder["type"]) => {
    return _.find(shutdownDates, shutdownInfo => {
      if (shutdownInfo.date === moment.utc(date).format("YYYY-MM-DD")) {
        // if we dont know the order type return the shutdown
        if (!shutdownInfo.shutdown.options?.orderTypesToAllow?.length || !orderType) {
          return true;
        } else {
          // otherwise check if the order type matches and return only if its not in the array
          return !shutdownInfo.shutdown.options?.orderTypesToAllow.includes(orderType);
        }
      }

      return false;
    });
  }, [ shutdownDates ]);

  return {
    shutdownDates,
    checkShutdownDate
  };
};
